/* BIMI-Verwaltung für Souvera-Clouds (Superadmin-only).
 *
 * Upload von Marken-Logo (SVG → Tiny-PS-Sanitizer) + VMC/CMC-Zertifikat,
 * Anzeige des generierten `default._bimi`-DNS-Records, Live-Verifikation
 * (DNS/DMARC/HTTPS-Erreichbarkeit) sowie Löschen der Assets.
 * Backend: /api/admin/clouds/{id}/bimi[/logo|/vmc|/check].
 */
import React, { useRef, useState } from "react";
import useSWR from "swr";
import { api, formatApiError } from "@/lib/api";
import {
    ShieldCheck, Upload, Trash2, RefreshCcw, AlertTriangle, CheckCircle2,
    XCircle, BadgeCheck, Copy, Image as ImageIcon,
} from "lucide-react";

const fetcher = (u) => api.get(u).then((r) => r.data);

function Row({ label, children, testid }) {
    return (
        <div className="flex items-start justify-between gap-3 py-1.5 border-b border-zinc-800/60 last:border-0">
            <span className="text-xs text-zinc-500 shrink-0">{label}</span>
            <span data-testid={testid} className="text-xs text-zinc-200 text-right break-all">
                {children}
            </span>
        </div>
    );
}

export default function CloudBimiPanel({ cloudId }) {
    const key = `/api/admin/clouds/${cloudId}/bimi`;
    const { data: st, mutate, isLoading } = useSWR(key, fetcher);
    const svgRef = useRef(null);
    const vmcRef = useRef(null);
    const [title, setTitle] = useState("");
    const [busy, setBusy] = useState(null); // 'logo' | 'vmc' | 'check' | 'del-logo' | 'del-vmc'
    const [err, setErr] = useState(null);
    const [report, setReport] = useState(null);
    const [checks, setChecks] = useState(null);

    const run = async (kind, fn) => {
        setBusy(kind);
        setErr(null);
        try {
            await fn();
        } catch (e) {
            setErr(formatApiError(e));
        }
        setBusy(null);
    };

    const uploadLogo = () =>
        run("logo", async () => {
            const file = svgRef.current?.files?.[0];
            if (!file) { setErr("Please choose an SVG file."); return; }
            const fd = new FormData();
            fd.append("file", file);
            if (title) fd.append("title", title);
            const { data } = await api.post(`${key}/logo`, fd, {
                headers: { "Content-Type": undefined },
            });
            setReport({ kind: "svg", ...(data.report || {}) });
            setChecks(null);
            if (svgRef.current) svgRef.current.value = "";
            await mutate();
        });

    const uploadVmc = () =>
        run("vmc", async () => {
            const file = vmcRef.current?.files?.[0];
            if (!file) { setErr("Please choose a PEM file."); return; }
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post(`${key}/vmc`, fd, {
                headers: { "Content-Type": undefined },
            });
            setReport({ kind: "vmc", ...(data.report || {}) });
            setChecks(null);
            if (vmcRef.current) vmcRef.current.value = "";
            await mutate();
        });

    const delLogo = () =>
        run("del-logo", async () => {
            if (!window.confirm("Remove logo? BIMI will be fully disabled as a result (the DNS record disappears)."))
                return;
            await api.delete(`${key}/logo`);
            setReport(null); setChecks(null);
            await mutate();
        });

    const delVmc = () =>
        run("del-vmc", async () => {
            if (!window.confirm("Remove certificate? The `a=` part of the BIMI record is dropped (logo remains)."))
                return;
            await api.delete(`${key}/vmc`);
            setReport(null); setChecks(null);
            await mutate();
        });

    const check = () =>
        run("check", async () => {
            const { data } = await api.get(`${key}/check`);
            setChecks(data);
        });

    const copyRecord = () => {
        if (st?.record?.value) navigator.clipboard?.writeText(st.record.value);
    };

    return (
        <div data-testid="cloud-bimi-panel" className="cm-card p-5">
            <div className="cm-h3 mb-1 flex items-center gap-2">
                <ShieldCheck size={14} className="text-zinc-400" />
                BIMI — Brand logo for email
            </div>
            <p className="text-xs text-zinc-400 mb-4">
                Stores a brand logo (SVG Tiny-PS) and optionally a VMC/CMC certificate.
                The generated <code className="text-zinc-300">default._bimi</code> record is
                also output among the cloud's DNS records. Requires{" "}
                <strong>DMARC ≥ quarantine</strong>.
            </p>

            {isLoading && <div className="text-xs text-zinc-500">Loading…</div>}

            {st && (
                <>
                    {/* Status + Vorschau */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                        <div className="md:col-span-2 bg-zinc-950 border border-zinc-800 rounded p-3">
                            <Row label="Status" testid="bimi-status">
                                {st.enabled ? (
                                    <span className="inline-flex items-center gap-1 text-emerald-400">
                                        <BadgeCheck size={12} /> active
                                    </span>
                                ) : (
                                    <span className="text-zinc-500">inactive (no logo)</span>
                                )}
                            </Row>
                            <Row label="Mail-Domain" testid="bimi-domain">
                                {st.mail_domain || "—"}
                            </Row>
                            <Row label="Logo" testid="bimi-has-logo">
                                {st.has_logo ? "uploaded" : "—"}
                                {st.svg_uploaded_at && (
                                    <span className="text-zinc-500"> · {String(st.svg_uploaded_at).slice(0, 10)}</span>
                                )}
                            </Row>
                            <Row label="Zertifikat (VMC/CMC)" testid="bimi-has-vmc">
                                {st.has_vmc ? "uploaded" : "—"}
                                {st.vmc_info?.expires && (
                                    <span className="text-zinc-500"> · valid until {String(st.vmc_info.expires).slice(0, 10)}</span>
                                )}
                            </Row>
                            {st.vmc_info && st.vmc_info.is_vmc === false && (
                                <div className="mt-2 flex items-start gap-1.5 text-amber-300 text-xs">
                                    <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                                    <span>Not a real VMC (no logotype extension) — Gmail/Yahoo only show the logo with a valid VMC.</span>
                                </div>
                            )}
                        </div>
                        <div className="bg-zinc-950 border border-zinc-800 rounded p-3 flex flex-col items-center justify-center gap-2">
                            <span className="text-xs text-zinc-500">Preview</span>
                            {st.svg_url ? (
                                <img
                                    data-testid="bimi-logo-preview"
                                    src={st.svg_url}
                                    alt="BIMI-Logo"
                                    className="w-20 h-20 rounded bg-white p-1 object-contain"
                                />
                            ) : (
                                <div className="w-20 h-20 rounded border border-dashed border-zinc-700 flex items-center justify-center text-zinc-600">
                                    <ImageIcon size={22} />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* DNS-Record */}
                    {st.record && (
                        <div className="mb-4">
                            <div className="flex items-center justify-between mb-1">
                                <span className="text-xs text-zinc-500">
                                    DNS-Record — <span className="text-zinc-300">{st.record.name}</span> (TXT)
                                </span>
                                <button
                                    data-testid="bimi-copy-record"
                                    onClick={copyRecord}
                                    className="text-xs text-zinc-400 hover:text-zinc-200 inline-flex items-center gap-1"
                                >
                                    <Copy size={11} /> Copy
                                </button>
                            </div>
                            <div
                                data-testid="bimi-record-value"
                                className="bg-black border border-zinc-800 rounded p-2 text-xs font-mono text-emerald-300 break-all"
                            >
                                {st.record.value}
                            </div>
                        </div>
                    )}

                    {/* Upload Logo */}
                    <div className="mb-3">
                        <div className="text-xs text-zinc-400 mb-1.5 font-medium">Upload logo (SVG)</div>
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                            <input
                                data-testid="bimi-logo-file"
                                ref={svgRef}
                                type="file"
                                accept=".svg,image/svg+xml"
                                className="text-xs text-zinc-300 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:bg-zinc-800 file:text-zinc-200 flex-1"
                            />
                            <input
                                data-testid="bimi-logo-title"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder="Title (optional)"
                                className="cm-input text-xs bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-zinc-200 w-full sm:w-40"
                            />
                            <button
                                data-testid="bimi-logo-upload-btn"
                                onClick={uploadLogo}
                                disabled={busy === "logo"}
                                className="cm-btn-secondary text-xs flex items-center gap-1.5 shrink-0 justify-center"
                            >
                                <Upload size={11} />
                                {busy === "logo" ? "Loading…" : "Upload"}
                            </button>
                            {st.has_logo && (
                                <button
                                    data-testid="bimi-logo-delete-btn"
                                    onClick={delLogo}
                                    disabled={busy === "del-logo"}
                                    className="cm-btn-danger text-xs flex items-center gap-1.5 shrink-0 justify-center"
                                >
                                    <Trash2 size={11} /> Delete
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Upload VMC */}
                    <div className="mb-4">
                        <div className="text-xs text-zinc-400 mb-1.5 font-medium">Upload certificate (VMC/CMC · PEM)</div>
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                            <input
                                data-testid="bimi-vmc-file"
                                ref={vmcRef}
                                type="file"
                                accept=".pem,.crt,application/x-pem-file"
                                className="text-xs text-zinc-300 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:bg-zinc-800 file:text-zinc-200 flex-1"
                            />
                            <button
                                data-testid="bimi-vmc-upload-btn"
                                onClick={uploadVmc}
                                disabled={busy === "vmc"}
                                className="cm-btn-secondary text-xs flex items-center gap-1.5 shrink-0 justify-center"
                            >
                                <Upload size={11} />
                                {busy === "vmc" ? "Loading…" : "Upload"}
                            </button>
                            {st.has_vmc && (
                                <button
                                    data-testid="bimi-vmc-delete-btn"
                                    onClick={delVmc}
                                    disabled={busy === "del-vmc"}
                                    className="cm-btn-danger text-xs flex items-center gap-1.5 shrink-0 justify-center"
                                >
                                    <Trash2 size={11} /> Delete
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Live-Check */}
                    <div className="flex items-center gap-2 mb-2">
                        <button
                            data-testid="bimi-check-btn"
                            onClick={check}
                            disabled={busy === "check" || !st.enabled}
                            className="cm-btn-secondary text-xs flex items-center gap-1.5"
                        >
                            <RefreshCcw size={11} className={busy === "check" ? "animate-spin" : ""} />
                            {busy === "check" ? "Checking…" : "Check live (DNS/DMARC/HTTPS)"}
                        </button>
                        {!st.enabled && (
                            <span className="text-xs text-zinc-500">Upload a logo first.</span>
                        )}
                    </div>

                    {checks && (
                        <div data-testid="bimi-check-result" className="bg-zinc-950 border border-zinc-800 rounded p-2.5 mb-2 space-y-1.5">
                            {(checks.checks || []).map((c, i) => (
                                <div key={i} className="flex items-start gap-1.5 text-xs">
                                    {c.ok ? (
                                        <CheckCircle2 size={13} className="text-emerald-400 mt-0.5 shrink-0" />
                                    ) : (
                                        <XCircle size={13} className="text-rose-400 mt-0.5 shrink-0" />
                                    )}
                                    <div>
                                        <div className="text-zinc-200">{c.name}</div>
                                        {c.detail && <div className="text-zinc-500 break-all">{c.detail}</div>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Sanitizer-/VMC-Report */}
                    {report && report.kind === "svg" && (
                        <div data-testid="bimi-svg-report" className="bg-zinc-950 border border-zinc-800 rounded p-2.5 mb-2 text-xs text-zinc-300">
                            <div className="text-emerald-400 flex items-center gap-1.5 mb-1">
                                <CheckCircle2 size={12} /> Logo processed ({report.size_bytes} bytes)
                            </div>
                            {report.removed_elements && Object.keys(report.removed_elements).length > 0 && (
                                <div className="text-amber-300">
                                    Removed elements:{" "}
                                    {Object.entries(report.removed_elements).map(([k, v]) => `${k}×${v}`).join(", ")}
                                </div>
                            )}
                            {report.removed_attrs > 0 && (
                                <div className="text-amber-300">Removed attributes: {report.removed_attrs}</div>
                            )}
                        </div>
                    )}
                    {report && report.kind === "vmc" && (
                        <div data-testid="bimi-vmc-report" className="bg-zinc-950 border border-zinc-800 rounded p-2.5 mb-2 text-xs text-zinc-300">
                            <div className="text-emerald-400 flex items-center gap-1.5 mb-1">
                                <CheckCircle2 size={12} /> Certificate valid
                            </div>
                            <div className="text-zinc-400">Subject: {report.subject_cn || "—"}</div>
                            <div className="text-zinc-400">Issuer: {report.issuer_cn || "—"}</div>
                            <div className="text-zinc-400">Valid until: {String(report.expires || "").slice(0, 10)}</div>
                            {(report.warnings || []).map((w, i) => (
                                <div key={i} className="text-amber-300 flex items-start gap-1.5 mt-1">
                                    <AlertTriangle size={11} className="mt-0.5 shrink-0" /> {w}
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}

            {err && (
                <div
                    data-testid="bimi-error"
                    className="mt-2 bg-rose-950/30 border border-rose-800 text-rose-300 text-xs p-2 font-mono break-all"
                >
                    {err}
                </div>
            )}
        </div>
    );
}
